import Sugar from 'sugar';
import type { ButtressDbService } from '@buttress/crag';

import type Company from '../models/company.js';
import type CommercialRelationship from '../models/commercial-relationship.js';
import type { CommercialRelationshipParty } from '../models/commercial-relationship.js';
import { createLogger } from '../ns-services/logger.js';

const logger = createLogger('shareable-connections');

export const CONNECTIONS_QUERY_LIMIT = 500;

export interface ShareableCompany {
  companyId: string;
  name: string;
  partyType: string;
}

export interface ShareableRelationship {
  relationshipId: string;
  companies: ShareableCompany[];
}

export interface ShareableRow {
  relationshipId: string;
  parties: CommercialRelationshipParty[];
}

export type ShareDirection = 'DOWN' | 'UP';

const isAccepted = (party: CommercialRelationshipParty) => (party.status ?? 'ACCEPTED') === 'ACCEPTED';
const isNotDeclined = (party: CommercialRelationshipParty) => (party.status ?? 'ACCEPTED') !== 'DECLINED';

const grantorPartyTypeByDirection: Record<ShareDirection, string> = { DOWN: 'CLIENT', UP: 'SUPPLIER' };

// UP reveals only who the grantor supplies; its co-suppliers on that row stay hidden.
function exposedParties(
  relationship: CommercialRelationship,
  grantorId: string,
  direction: ShareDirection,
): CommercialRelationshipParty[] {
  return relationship.parties.filter(
    (p) => p.companyId !== grantorId && isNotDeclined(p) && (direction === 'DOWN' || p.partyType === 'CLIENT'),
  );
}

// The lambdas share the picked rows by id, so DOWN must match their otherRows filter exactly.
export function shareableRows(
  grantorRelationships: CommercialRelationship[],
  grantorId: string,
  requesterId: string,
  direction: ShareDirection,
): ShareableRow[] {
  return grantorRelationships
    .filter((r) => {
      if (r.status !== 'ACTIVE') return false;
      if (r.parties.some((p) => p.companyId === requesterId)) return false;
      const grantorParty = r.parties.find((p) => p.companyId === grantorId);
      return (
        !!grantorParty && isAccepted(grantorParty) && grantorParty.partyType === grantorPartyTypeByDirection[direction]
      );
    })
    .map((r) => ({ relationshipId: r.id, parties: exposedParties(r, grantorId, direction) }))
    .filter((r) => r.parties.length > 0);
}

export function shareableCompanyIds(rows: ShareableRow[]): string[] {
  return [...new Set(rows.flatMap((r) => r.parties.map((p) => p.companyId)))];
}

export function toShareableRelationships(
  rows: ShareableRow[],
  companies: Pick<Company, 'id' | 'name'>[],
): ShareableRelationship[] {
  const companiesById = new Map(companies.map((c) => [c.id, c]));
  return rows
    .map((r) => ({
      relationshipId: r.relationshipId,
      companies: r.parties.flatMap((party) => {
        const company = companiesById.get(party.companyId);
        return company ? [{ companyId: party.companyId, name: company.name, partyType: party.partyType }] : [];
      }),
    }))
    .filter((r) => r.companies.length > 0);
}

export async function loadShareableConnections(
  db: ButtressDbService,
  grantorId: string,
  requesterId: string,
  direction: ShareDirection,
): Promise<ShareableRelationship[]> {
  const relationshipsQuery = await db.query(
    'comRelationship',
    { status: { $eq: 'ACTIVE' }, 'parties.companyId': { $eq: grantorId } },
    { limit: CONNECTIONS_QUERY_LIMIT },
  );
  const relationships = relationshipsQuery.results as unknown as CommercialRelationship[];
  if (relationships.length >= CONNECTIONS_QUERY_LIMIT) {
    logger.warn(
      `Company ${grantorId} has ${CONNECTIONS_QUERY_LIMIT}+ connections — checklist truncated to the first ${CONNECTIONS_QUERY_LIMIT}.`,
    );
  }

  const rows = shareableRows(relationships, grantorId, requesterId, direction);
  const companyIds = shareableCompanyIds(rows);
  if (companyIds.length === 0) return [];

  const companiesQuery = await db.query('company', { id: { $in: companyIds } }, { limit: CONNECTIONS_QUERY_LIMIT });
  return toShareableRelationships(rows, companiesQuery.results as unknown as Company[]);
}

export function connectionLabel(relationship: ShareableRelationship): string {
  return relationship.companies
    .map((c) => `${c.name} (${Sugar.String.capitalize(c.partyType, true, true)})`)
    .join(', ');
}

export function searchConnections(
  connections: ShareableRelationship[],
  selected: ReadonlySet<string>,
  term: string,
): ShareableRelationship[] {
  const needle = term.trim().toLowerCase();
  const notSelected = connections.filter((r) => !selected.has(r.relationshipId));
  if (!needle) return notSelected;
  return notSelected.filter((r) => r.companies.some((c) => c.name.toLowerCase().includes(needle)));
}
